import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { Calendar, MapPin, Zap } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';

// Score Table - Teams as rows, Sets as columns (No trophy icons, no sets summary)
const MatchScoreTable = ({ match, teamAPlayer1, teamAPlayer2, teamBPlayer1, teamBPlayer2, language, t }) => {
  const isTeamAWinner = match.winner_team === 'A';
  const sets = match.sets || [];
  
  // Check if it's a super tiebreak (3rd set after 1-1)
  const isSuperTiebreak = sets.length === 3 && 
    sets.slice(0, 2).filter(s => s.team_a > s.team_b).length === 1;

  // Greek doesn't use uppercase for better readability
  const headerClass = language === 'el' 
    ? 'text-xs font-semibold tracking-wider text-muted-foreground'
    : 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr>
            <th className={`${headerClass} text-left py-2 px-3`}>
              {t('team')}
            </th>
            {sets.map((_, idx) => (
              <th key={`set-header-${idx}`} className={`${headerClass} text-center py-2 px-2 w-12`}>
                {idx === 2 && isSuperTiebreak ? (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400">
                    STB
                  </span>
                ) : (
                  `S${idx + 1}`
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Team A Row */}
          <tr className={`border-b border-border ${isTeamAWinner ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`}>
            <td className="py-3 px-3">
              <div className="flex items-center gap-2">
                {isTeamAWinner && <div className="w-0.5 h-8 bg-green-500 rounded-full flex-shrink-0" />}
                <div className="min-w-0">
                  <p className={`text-sm truncate ${isTeamAWinner ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    {teamAPlayer1?.name || '?'}
                  </p>
                  <p className={`text-sm truncate ${isTeamAWinner ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    {teamAPlayer2?.name || '?'}
                  </p>
                </div>
              </div>
            </td>
            {sets.map((set, idx) => {
              const won = set.team_a > set.team_b;
              return (
                <td key={`set-a-${idx}`} className="py-3 px-2 text-center">
                  <span className={`text-sm font-mono ${
                    won ? 'text-green-600 dark:text-green-400 font-bold' : 'text-muted-foreground'
                  }`}>
                    {set.team_a}
                  </span>
                </td>
              );
            })}
          </tr>
          
          {/* Team B Row */}
          <tr className={`${!isTeamAWinner ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`}>
            <td className="py-3 px-3">
              <div className="flex items-center gap-2">
                {!isTeamAWinner && <div className="w-0.5 h-8 bg-green-500 rounded-full flex-shrink-0" />}
                <div className="min-w-0">
                  <p className={`text-sm truncate ${!isTeamAWinner ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    {teamBPlayer1?.name || '?'}
                  </p>
                  <p className={`text-sm truncate ${!isTeamAWinner ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    {teamBPlayer2?.name || '?'}
                  </p>
                </div>
              </div>
            </td>
            {sets.map((set, idx) => {
              const won = set.team_b > set.team_a;
              return (
                <td key={`set-b-${idx}`} className="py-3 px-2 text-center">
                  <span className={`text-sm font-mono ${
                    won ? 'text-green-600 dark:text-green-400 font-bold' : 'text-muted-foreground'
                  }`}>
                    {set.team_b}
                  </span>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default function Scores() {
  const { t, language } = useLanguage();
  const { matches, getPlayerById, loading, ladder } = useLadder();

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return format(date, 'dd MMM yyyy', { locale: language === 'el' ? el : enUS });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="scores-loading">
        <Skeleton className="h-10 w-48 mb-8" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Filter matches by current period dates
  const currentPeriodMatches = (() => {
    // If period not configured, return empty array
    if (!ladder?.period_start_date || !ladder?.period_end_date) {
      return [];
    }
    
    const periodStart = new Date(ladder.period_start_date);
    const periodEnd = new Date(ladder.period_end_date);
    periodEnd.setHours(23, 59, 59, 999); // Include entire end day
    
    return matches.filter(m => {
      const matchDate = m.date ? new Date(m.date) : new Date(m.created_at);
      return matchDate >= periodStart && matchDate <= periodEnd;
    });
  })();

  // Use current period matches only (already sorted by date DESC from backend)
  const sortedMatches = currentPeriodMatches;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="scores-page">
      <h1 className="font-heading text-2xl font-bold text-foreground mb-6">{t('matchHistory')}</h1>

      {sortedMatches.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border" data-testid="no-matches">
          <p className="text-muted-foreground">{t('noMatches')}</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="matches-list">
          {sortedMatches.map((match, index) => {
            const teamAPlayer1 = getPlayerById(match.team_a_player1_id);
            const teamAPlayer2 = getPlayerById(match.team_a_player2_id);
            const teamBPlayer1 = getPlayerById(match.team_b_player1_id);
            const teamBPlayer2 = getPlayerById(match.team_b_player2_id);
            
            const sets = match.sets || [];
            const isSuperTiebreak = sets.length === 3 && 
              sets.slice(0, 2).filter(s => s.team_a > s.team_b).length === 1;

            return (
              <div 
                key={match.id} 
                className="bg-card rounded-xl border border-border overflow-hidden"
                data-testid={`match-card-${index}`}
              >
                {/* Match metadata */}
                <div className="flex items-center justify-between gap-4 px-4 py-2.5 bg-muted/50 border-b border-border text-sm text-muted-foreground">
                  <div className="flex items-center gap-4">
                    {match.date && (
                      <span className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(match.date)}
                      </span>
                    )}
                    {match.court && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        {match.court}
                      </span>
                    )}
                  </div>
                  {isSuperTiebreak && (
                    <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs font-medium">
                      <Zap className="h-3 w-3" />
                      STB
                    </span>
                  )}
                </div>

                {/* Score Table */}
                <div className="p-3">
                  <MatchScoreTable
                    match={match}
                    teamAPlayer1={teamAPlayer1}
                    teamAPlayer2={teamAPlayer2}
                    teamBPlayer1={teamBPlayer1}
                    teamBPlayer2={teamBPlayer2}
                    language={language}
                    t={t}
                  />

                  {/* Comments */}
                  {match.comments && (
                    <p className="mt-3 pt-3 border-t border-border text-sm text-muted-foreground italic">
                      {match.comments}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
